/**
 * API iki sınıf zaman döner:
 *  (a) UTC-naive: `toDateTimeString()` çıktısı ("2026-07-27 19:39:00") — aslında UTC,
 *      `new Date()` ile parse edilirse tarayıcı yerel saati sanılır (TR'de 3 saat erken görünür).
 *  (b) Duvar saati: kullanıcı girdisi (takvim start/end, due_date, taken_at vb.) — yerel parse DOĞRU,
 *      bu helper'lara SOKULMAMALI.
 * Z'li / offset'li ISO string'ler (mesaj, bildirim, timeline) her iki yolda da doğru parse edilir.
 */

/** Naive "YYYY-MM-DD HH:mm:ss" (UTC) veya Z/offset'li ISO'yu doğru Date'e çevirir. */
export function parseUtcDateTime(value: string): Date {
  const v = value.trim();
  // Z veya +hh:mm / +hhmm offset'i zaten varsa olduğu gibi parse et.
  if (/([zZ]|[+-]\d{2}:?\d{2})$/.test(v)) return new Date(v);
  let iso = v.includes("T") ? v : v.replace(" ", "T");
  if (!iso.includes("T")) iso = `${iso}T00:00:00`;
  return new Date(`${iso}Z`);
}

const TR_TIME_ZONE = "Europe/Istanbul";

/** UTC-sınıfı bir API zamanını tr-TR formatında, gerçek Türkiye saatiyle döndürür. */
export function formatDateTimeTR(
  value: string | null | undefined,
  opts?: Intl.DateTimeFormatOptions,
): string {
  if (!value) return "-";
  const d = parseUtcDateTime(value);
  if (Number.isNaN(d.getTime())) return value;
  // dateStyle/timeStyle verildiyse day/month/year/hour/minute ile karıştırılamaz (Intl TypeError).
  const usesStyle = Boolean(opts?.dateStyle || opts?.timeStyle);
  const base: Intl.DateTimeFormatOptions = usesStyle
    ? {}
    : { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" };
  return d.toLocaleString("tr-TR", { timeZone: TR_TIME_ZONE, ...base, ...opts });
}

/** UTC-sınıfı bir API zamanını Türkiye gün anahtarına (YYYY-MM-DD) çevirir. */
export function toLocalDateKeyTR(value: string): string {
  return parseUtcDateTime(value).toLocaleDateString("sv-SE", { timeZone: TR_TIME_ZONE });
}

export function formatTimelineDate(isoDate: string): string {
  const d = new Date(isoDate);
  const today = new Date();
  const target = new Date(d);

  const diff = Math.round(
    (new Date(target.toLocaleDateString("en-US", { timeZone: "Europe/Istanbul" })).getTime() -
      new Date(today.toLocaleDateString("en-US", { timeZone: "Europe/Istanbul" })).getTime()) /
      (1000 * 60 * 60 * 24),
  );

  if (diff === 0) return "Bugün";
  if (diff === -1) return "Dün";
  if (diff === 1) return "Yarın";

  return d.toLocaleDateString("tr-TR", {
    timeZone: "Europe/Istanbul",
    day: "numeric",
    month: "short",
  });
}

export function formatFullDate(isoDate: string): string {
  const d = new Date(isoDate);
  return d.toLocaleDateString("tr-TR", {
    timeZone: "Europe/Istanbul",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function isSameDay(a: string, b: string): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" }) ===
    db.toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" })
  );
}
